<template>
  <div class="search-wrapper ml-2">
    <div class="search-filter">
      <div class="d-flex align-items-center">
        <page-title class="ml-1">Search</page-title>
        <checkbox
          class="ml-3"
          label="Show Expired"
          name="showExpired"
          id="showExpired"
          v-model="showExpired"
        />
        <icon-button
          icon="info-circle"
          v-tooltip="
            'Show expired Prefixes, Protocols, Results Macros, Case Tags, and Hold Codes, as well as inactive pathologists.'
          "
        />
      </div>
      <form @submit.prevent="handleCreateSearch" class="searchHeader">
        <div class="row ml-2 mb-3">
          <div class="my-2 my-lg-0 d-flex">
            <loader size="small" class="mr-2 align-self-center" v-show="isLoading" />
            <button
              v-if="loadedSearch"
              @click.prevent="deleteSavedSearch"
              class="btn btn-danger mr-1"
            >
              Delete Saved Search
            </button>
            <button @click="openSaveSearch" class="btn btn-primary mr-1 py-0">
              Save Search As
              <DxDropDownButton
                v-if="userSearches.length"
                icon="chevrondown"
                type="normal"
                :useSelectMode="false"
                :showArrowIcon="false"
                :split-button="false"
                :use-select-mode="true"
                stylingMode="text"
                class="btn btn-primary dropdown-button p-0"
                @item-click="loadSearch"
                :items="userSearches"
                displayExpr="name"
                valueExpr="id"
                :elementAttr="buttonAttr"
                :wrapItemText="false"
                :drop-down-options="dropDownOptions"
                :itemTemplate="itemTemplate"
              >
                <template #itemTemplate="{ data }">
                  <div>
                    {{ data.name }}
                  </div>
                </template>
              </DxDropDownButton>
            </button>
            <button
              type="button"
              @click="clearFilters"
              icon="search"
              class="btn btn-outline-danger mr-1"
            >
              <span class="ml-1">Clear Filters</span>
            </button>
            <icon-button :disable="isLoading" type="submit" icon="search" class="btn-primary mr-1">
              <span class="ml-1">Search</span></icon-button
            >
            <button :disable="isLoading" class="btn btn-primary mr-1" @click.prevent="handleExport">
              Export
            </button>
            <button
              :disable="isLoading"
              class="btn btn-primary"
              @click.prevent="toggleExportsPopup"
            >
              Download Exports
            </button>
          </div>
        </div>
        <h2 class="pb-2">Filters</h2>
        <div class="row">
          <div class="col-lg-6 col-xl-3">
            <div class="row" v-if="hasPermission('PatientName')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Patient Name</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                name="patientName"
                id="patientName"
                :highlightSearchValue="true"
                placeholder="Last, First"
                v-model="searchFields.patientFieldContains"
                data-private="redact"
              />
            </div>
            <div class="row" v-if="hasPermission('CaseNumber')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Accession #</b></div>
              <text-input
                class="col input-field pr-1"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="From"
                name="caseNumberStart"
                ref="caseNumberStart"
                @blur="formatAccessionNumber"
                v-model="caseNumberStart"
              />
              <text-input
                class="col input-field pl-1"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="To"
                name="caseNumberEnd"
                ref="caseNumberEnd"
                @blur="formatAccessionNumber"
                v-model="caseNumberEnd"
              />
            </div>
            <div class="row" v-if="hasPermission('LabLocation')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Lab Location</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                name="labLocation"
                id="labLocation"
                :highlightSearchValue="true"
                v-model="searchFields.labLocationIds"
                :items="availableLabLocations"
              />
            </div>
            <div class="row" v-if="hasPermission('Prefix')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Prefix</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.prefixIds"
                :highlightSearchValue="true"
                :dataSource="prefixDataSource"
                displayExpr="code"
                :grayOutExpired="true"
              />
            </div>
            <div class="row" v-if="hasPermission('PrefixTags')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Prefix Tags</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                v-model="searchFields.prefixTagIds"
                :dataSource="prefixTagOptions"
              />
            </div>
            <div class="row" v-if="hasPermission('PatientDateOfBirth')">
              <div class="col-lg-4 col-xl-3 field-label"><b>DOB</b></div>
              <date-picker
                class="col input-field"
                ref="bornOn"
                :noLabel="true"
                id="bornOn"
                :highlightSearchValue="true"
                dateSerializationFormat="yyyy-MM-dd"
                v-model="searchFields.bornOn"
                name="bornOn"
                data-private="redact"
                :twoDigitYear="true"
              />
            </div>
            <div class="row" v-if="hasPermission('PatientAge')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Age (years)</b></div>
              <text-input
                class="col input-field pr-1"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="From"
                v-model="searchFields.ageMinYears"
              />
              <text-input
                class="col input-field pl-1"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="To"
                v-model="searchFields.ageMaxYears"
              />
            </div>
            <div class="row" v-if="hasPermission('PatientGender')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Gender</b></div>
              <tag-input
                class="col input-field"
                name="sex"
                ref="genderIds"
                :noLabel="true"
                :highlightSearchValue="true"
                id="sex"
                :items="sexes"
                v-model="searchFields.genderIds"
              />
            </div>
            <div class="row" v-if="hasPermission('PatientRace')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Race</b></div>
              <tag-input
                class="col input-field"
                name="race"
                ref="raceIds"
                :noLabel="true"
                :highlightSearchValue="true"
                id="raceIds"
                :items="races"
                v-model="searchFields.raceIds"
              />
            </div>
            <div class="row" v-if="hasPermission('PatientSSN')">
              <div class="col-lg-4 col-xl-3 field-label"><b>SSN</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                name="ssn"
                id="ssn"
                :highlightSearchValue="true"
                v-model="searchFields.ssn"
                data-private="redact"
              />
            </div>
            <div class="row" v-if="hasPermission('PatientMRN')">
              <div class="col-lg-4 col-xl-3 field-label"><b>MRN</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                name="mrn"
                id="mrn"
                :highlightSearchValue="true"
                v-model="searchFields.mrn"
                data-private="redact"
              />
            </div>
            <div class="row" v-if="hasPermission('PatientAccountNumber')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Account #</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                name="accountNum"
                id="accountNum"
                :highlightSearchValue="true"
                v-model="searchFields.accountNum"
                data-private="redact"
              />
            </div>
            <div class="row" v-if="hasPermission('CaseOrderNumber')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Order #</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                name="orderNum"
                id="orderNum"
                :highlightSearchValue="true"
                v-model="searchFields.orderNum"
                data-private="redact"
              />
            </div>
          </div>
          <div class="col-lg-6 col-xl-3">
            <div class="row" v-if="hasPermission('ProviderName')">
              <div class="col-lg-4 col-xl-3 field-label d-flex justify-content-between">
                <button
                  v-tooltip.top="primaryTooltip"
                  type="button"
                  class="py-0 px-1 btn"
                  @click="toggleSecondaryProviders"
                  :tabindex="tabProp"
                >
                  <icon
                    v-if="searchFields.searchPrimaryProviders"
                    class="text-primary"
                    icon="parking"
                  />
                  <span
                    v-if="searchFields.searchSecondaryProviders"
                    class="secondary-icon font-weight-bold"
                    >S</span
                  ></button
                ><b>Provider</b>
              </div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                placeholder="Last, First"
                :highlightSearchValue="true"
                :dataSource="createArrayStore('providerNamesList')"
                valueExpr="id"
                v-model="searchFields.providerIds"
              />
            </div>
            <div class="row" v-if="hasPermission('ProviderLocationId')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Provider Location</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :dataSource="createArrayStore('locationsNamesList')"
                displayExpr="displayName"
                valueExpr="id"
                v-model="searchFields.providerLocationIds"
              />
            </div>
            <div class="row" v-if="hasPermission('ProviderLocationCity')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Provider City</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :items="providers.cities"
                :highlightSearchValue="true"
                displayExpr="displayName"
                valueExpr="displayName"
                v-model="searchFields.providerLocationCities"
              />
            </div>
            <div class="row" v-if="hasPermission('ProviderLocationState')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Provider State</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :items="states"
                displayExpr="displayName"
                v-model="searchFields.providerLocationStates"
              />
            </div>
            <div class="row" v-if="hasPermission('ProviderLocationZip')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Provider ZIP</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :items="providers.zips"
                :highlightSearchValue="true"
                displayExpr="displayName"
                valueExpr="displayName"
                v-model="searchFields.providerLocationZipCodes"
              />
            </div>
            <div class="row" v-if="hasPermission('PathologistName')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Pathologist</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                placeholder="Last, First"
                :highlightSearchValue="true"
                v-model="searchFields.pathologistIds"
                :dataSource="pathologistsDataSource"
                searchExpr="displayName"
                :grayOutExpired="true"
              />
            </div>
            <div class="row" v-if="hasPermission('Proxy')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Proxy</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                placeholder="Last, First"
                :highlightSearchValue="true"
                v-model="searchFields.proxyUserIds"
                :dataSource="pathologistsDataSource"
                searchExpr="displayName"
                :grayOutExpired="true"
              />
            </div>
            <div class="row" v-if="hasPermission('Protocol')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Protocol</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                v-model="searchFields.protocolIds"
                :dataSource="protocolDataSource"
                displayExpr="macroName"
                searchExpr="macroName"
                valueExpr="macroId"
                :grayOutExpired="true"
              />
            </div>

            <div class="row" v-if="hasPermission('PreDiagnosis')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Pre-Diagnosis Text</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                placeholder="Clinical, gross"
                :highlightSearchValue="true"
                v-model="searchFields.prediagnosis"
              />
            </div>
            <div class="row" v-if="hasPermission('Diagnosis')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Diagnostic Text</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                placeholder="Diagnosis, microscopic, specimen note, case note"
                :highlightSearchValue="true"
                v-model="searchFields.diagnosis"
              />
            </div>

            <div class="row" v-if="hasPermission('CPT')">
              <div class="col-lg-4 col-xl-3 field-label"><b>CPT Code</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cptCodeIds"
                :highlightSearchValue="true"
                :dataSource="cptCodesDataSource"
                displayExpr="code"
                :grayOutExpired="true"
              />
            </div>
            <div class="row" v-if="hasPermission('SpecimenIcdCode')">
              <div class="col-lg-4 col-xl-3 field-label"><b>ICD10 Code</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.icdCodeIds"
                :highlightSearchValue="true"
                :dataSource="icdDataSource"
                searchExpr="displayName"
                :grayOutExpired="true"
              />
            </div>
            <div class="row" v-if="hasPermission('CaseTags')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Case Tags</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.caseTagIds"
                :highlightSearchValue="true"
                :dataSource="caseTagDataSource"
                searchExpr="displayName"
                :grayOutExpired="true"
              />
            </div>
            <div class="row" v-if="hasPermission('HoldCodes')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Hold Codes</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.holdCodeIds"
                :highlightSearchValue="true"
                :dataSource="holdCodeDataSource"
                :displayExpr="holdCodeDisplayExpr"
                :searchExpr="labSettings.SearchByHoldCode ? 'holdCode' : 'displayName'"
                :grayOutExpired="true"
              />
            </div>
            <div class="row" v-if="hasPermission('Procedures')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Orders</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.procedureIds"
                :highlightSearchValue="true"
                :dataSource="procedureDataSource"
                :displayExpr="procedureDisplayExpr"
                :searchExpr="procedureSearchExpr"
                :grayOutExpired="true"
              />
            </div>

            <div class="row" v-if="hasPermission('BillingTypes')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Billing Types</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :items="billingTypeOptions"
                :highlightSearchValue="true"
                v-model="searchFields.billingTypes"
              />
            </div>
            <div class="row" v-if="hasPermission('BillingCycles')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Billing Cycles</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :items="billingCycleOptions"
                :highlightSearchValue="true"
                v-model="searchFields.billingCycles"
              />
            </div>
          </div>
          <div class="col-xl-6 mt-2">
            <div class="row" v-if="hasPermission('CaseCollectedOn')">
              <div class="col-2 field-label"><b>Collected</b></div>
              <date-range
                ref="collectedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="collectedOnOrAfter"
                @input="handleCollectedRange"
                :start="searchFields.collectedOnOrAfter"
                :end="searchFields.collectedOnOrBefore"
              />
              <div class="col-6"></div>
            </div>
            <div
              v-if="hasPermission('CaseAccessionedOn') || hasPermission('AccessionedBy')"
              class="row"
            >
              <div class="col-2 field-label"><b>Accessioned</b></div>
              <date-range
                v-if="hasPermission('CaseAccessionedOn')"
                ref="accessionedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="accessionedOnOrAfter"
                @input="handleAccessionedRange"
                :start="searchFields.accessionedOnOrAfter"
                :end="searchFields.accessionedOnOrBefore"
              />
              <div v-if="hasPermission('AccessionedBy')" class="field-label">
                <b>By</b>
              </div>
              <tag-input
                v-if="hasPermission('AccessionedBy')"
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :dataSource="userDataSource"
                :displayExpr="userDisplayName"
                valueExpr="userName"
                v-model="searchFields.accessioners"
                :grayOutExpired="true"
              />
            </div>
            <div v-if="hasPermission('CaseReceivedOn')" class="row">
              <div class="col-2 field-label"><b>Received</b></div>
              <date-range
                ref="receivedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                @input="handleReceivedRange"
                name="receivedOnOrAfter"
                placeholder="Start Date"
                :start="searchFields.receivedOnOrAfter"
                :end="searchFields.receivedOnOrBefore"
              />
            </div>
            <div class="row" v-if="hasPermission('GrossedOn') || hasPermission('GrossedBy')">
              <div class="col-2 field-label"><b>Grossed</b></div>
              <date-range
                v-if="hasPermission('GrossedOn')"
                ref="grossedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="grossedOnOrAfter"
                @input="handleGrossedRange"
                :start="searchFields.grossedOnOrAfter"
                :end="searchFields.grossedOnOrBefore"
              />
              <div v-if="hasPermission('GrossedBy')" class="field-label"><b>By</b></div>
              <tag-input
                v-if="hasPermission('GrossedBy')"
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :dataSource="userDataSource"
                :displayExpr="userDisplayName"
                valueExpr="id"
                v-model="searchFields.grossers"
                :grayOutExpired="true"
              />
            </div>
            <div class="row" v-if="hasPermission('ResultedOn') || hasPermission('ResultedBy')">
              <div class="col-2 field-label"><b>Resulted</b></div>
              <date-range
                v-if="hasPermission('ResultedOn')"
                ref="resultedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Resulted Date"
                name="resultedOnOrAfter"
                @input="handleResultedOnRange"
                :start="searchFields.resultedOnOrAfter"
                :end="searchFields.resultedOnOrBefore"
              />
              <div v-if="hasPermission('ResultedBy')" class="field-label">
                <b>By</b>
              </div>
              <tag-input
                v-if="hasPermission('ResultedBy')"
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :dataSource="userDataSource"
                :displayExpr="userDisplayName"
                valueExpr="userName"
                v-model="searchFields.resulters"
                :grayOutExpired="true"
              />
            </div>
            <div v-if="hasPermission('SignedOn')" class="row">
              <div class="col-2 field-label"><b>First Signed</b></div>
              <date-range
                ref="signedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="firstSignedOnOrAfter"
                @input="handleSignedDateRange"
                :start="searchFields.firstSignedOnOrAfter"
                :end="searchFields.firstSignedOnOrBefore"
              />
            </div>
            <div v-if="hasPermission('SignedOn')" class="row">
              <div class="col-2 field-label"><b>Last Signed</b></div>
              <date-range
                ref="lastSignedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="lastSignedOnOrAfter"
                @input="handleLastSignedDateRange"
                :start="searchFields.lastSignedOnOrAfter"
                :end="searchFields.lastSignedOnOrBefore"
              />
            </div>
            <div class="row" v-if="hasPermission('ModifiedOn') || hasPermission('ModifiedBy')">
              <div class="col-2 field-label"><b>Modified After Reported On</b></div>
              <date-range
                v-if="hasPermission('ModifiedOn')"
                ref="modifiedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="modifiedOnOrAfter"
                @input="handleModifiedOnRange"
                :start="searchFields.modifiedOnOrAfter"
                :end="searchFields.modifiedOnOrBefore"
              />
              <div v-if="hasPermission('ModifiedBy')" class="field-label"><b>By</b></div>
              <tag-input
                v-if="hasPermission('ModifiedBy')"
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :dataSource="userDataSource"
                :displayExpr="userDisplayName"
                valueExpr="userName"
                v-model="searchFields.modifiers"
                :grayOutExpired="true"
              />
            </div>

            <div class="row" v-if="hasPermission('CaseStage')">
              <div class="col-2 field-label"><b>Status</b></div>
              <select-input
                class="col-3 input-field"
                :noLabel="true"
                v-model="searchFields.caseStageId"
                :items="caseStageOptions"
              />
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <h2 class="pb-2">Surgical Filters</h2>
          </div>
          <div class="col-lg-6 col-xl-3">
            <div class="row" v-if="hasPermission('SpecimenSite')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Site</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                v-model="searchFields.specimenSiteContains"
                :disabled="hasCytologyFilters"
              />
            </div>
            <div class="row" v-if="hasPermission('SpecimenResultMacroName')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Results Macro</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.resultsMacroIds"
                :highlightSearchValue="true"
                :dataSource="resultsMacrosDataSource"
                displayExpr="macroName"
                valueExpr="macroId"
                :grayOutExpired="true"
                :disabled="hasCytologyFilters"
              />
            </div>
            <div class="row" v-if="hasPermission('SpecimenResultMacroName')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Macro Description</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.macroDescriptionStartsWith"
                :highlightSearchValue="true"
                :disabled="hasCytologyFilters"
              />
            </div>
            <div class="row" v-if="hasPermission('InterfaceDiagnosis')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Interface Diagnosis</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                :dataSource="interfaceDiagnosesDataSource"
                :highlightSearchValue="true"
                valueExpr="displayName"
                v-model="searchFields.interfaceDiagnoses"
                :disabled="hasCytologyFilters"
              />
            </div>
          </div>
          <div class="col-lg-6">
            <div class="row" v-if="hasPermission('CaseReceivedOn')">
              <div class="col-2 field-label"><b>Reviewed On</b></div>
              <date-range
                v-if="hasPermission('CaseReceivedOn')"
                ref="reviewedRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="reviewedOnOrAfter"
                @input="handleReviewedOnRange"
                :start="searchFields.reviewedOnOrAfter"
                :end="searchFields.reviewedOnOrBefore"
                :disabled="hasCytologyFilters"
              />
              <div v-if="hasPermission('CaseReceivedOn')" class="field-label"><b>By</b></div>
              <tag-input
                v-if="hasPermission('CaseReceivedOn')"
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :dataSource="userDataSource"
                :displayExpr="userDisplayName"
                valueExpr="userName"
                v-model="searchFields.reviewers"
                :grayOutExpired="true"
                :disabled="hasCytologyFilters"
              />
            </div>
          </div>
        </div>
        <!-- Cytology Filters Section -->
        <div v-if="cytologyModuleEnabled" class="row mt-3">
          <div class="col-12">
            <h2 class="pb-2">Cytology Filters</h2>
          </div>
          <!-- Column 1: Diagnostic Fields -->
          <div class="col-lg-6 col-xl-3">
            <div class="row" v-if="hasPermission('CytDiagnosticMacros')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Diagnostic Macros</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytDiagnosticMacrosIds"
                :dataSource="cytDiagnosticMacrosDataSource"
                :highlightSearchValue="true"
              />
            </div>
            <template v-if="hasPermission('CytDiagnosticCodes')">
              <div v-for="group in cytologyDiagnosticCodeTypes" :key="group.fieldName" class="row">
                <div class="col-lg-4 col-xl-3 field-label">
                  <b>{{ group.label }}</b>
                </div>
                <tag-input
                  class="col input-field"
                  :noLabel="true"
                  v-model="searchFields[group.fieldName]"
                  :name="`${group.fieldName}`"
                  :dataSource="group.dataSource"
                  searchExpr="name"
                  displayExpr="name"
                  :highlightSearchValue="true"
                  :placeholder="`Select ${group.label}`"
                />
              </div>
            </template>

            <div class="row" v-if="hasPermission('CytSeverityLevels')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Severity Levels</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytSeverityLevels"
                :dataSource="cytSeverityLevelsDataSource"
                :highlightSearchValue="true"
              />
            </div>
            <div class="row" v-if="hasPermission('CytologyDiagnosticsFreeText')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Diagnostic Text</b></div>
              <text-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytologyDiagnosticsFreeText"
                :highlightSearchValue="true"
                placeholder="Enter diagnostic text"
              />
            </div>
            <div class="row" v-if="hasPermission('CytPrefixType')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Prefix Types</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytPrefixTypeIds"
                :dataSource="cytPrefixTypesDataSource"
                :highlightSearchValue="true"
              />
            </div>
          </div>
          <!-- Column 2: Molecular & Testing Fields -->
          <div class="col-lg-6 col-xl-3">
            <div class="row" v-if="hasPermission('CytMolecPanels')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Molecular Panels</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytMolecPanelsIds"
                :dataSource="cytMolecPanelsDataSource"
                :highlightSearchValue="true"
                displayExpr="displayText"
                searchExpr="displayText"
              />
            </div>
            <div class="row" v-if="hasPermission('CytMolecTests')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Molecular Tests</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytMolecTestsIds"
                :dataSource="cytMolecTestsDataSource"
                :highlightSearchValue="true"
                searchExpr="displayText"
                displayExpr="displayText"
              />
            </div>
            <div
              v-show="hasCytTestSelected"
              class="row"
              v-if="hasPermission('CytMolecTestResults')"
            >
              <div class="col-lg-4 col-xl-3 field-label"><b>Test Results</b></div>
              <tag-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytMolecTestResultsIds"
                :dataSource="cytMolecTestResultsDataSource"
                :highlightSearchValue="true"
              />
            </div>
            <div class="row" v-if="hasPermission('CytMolecTestingOnly')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Molecular Testing Only</b></div>
              <select-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytMolecTestingOnly"
                :items="yesNoOptions"
                placeholder="Select..."
                :clearable="true"
              />
            </div>
            <div class="row" v-if="hasPermission('CytAgreedWithLastReview')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Agreed with Last Review</b></div>
              <select-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytAgreedWithLastReview"
                :items="yesNoOptions"
                placeholder="Select..."
                :clearable="true"
              />
            </div>
            <div class="row" v-if="hasPermission('CytScreenAgain')">
              <div class="col-lg-4 col-xl-3 field-label"><b>Screen Again</b></div>
              <select-input
                class="col input-field"
                :noLabel="true"
                v-model="searchFields.cytScreenAgain"
                :items="yesNoOptions"
                placeholder="Select..."
                :clearable="true"
              />
            </div>
          </div>
          <!-- Column 3: Date Ranges & User Fields -->
          <div class="col-xl-6 mt-2">
            <div class="row" v-if="hasPermission('CytScreenDate') || hasPermission('CytScreeners')">
              <div class="col-2 field-label"><b>Screened</b></div>
              <date-range
                v-if="hasPermission('CytScreenDate')"
                ref="cytScreenDateRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="cytScreenDateAfter"
                @input="handleCytScreenDateRange"
                :start="searchFields.cytScreenDateAfter"
                :end="searchFields.cytScreenDateBefore"
              />
              <div v-if="hasPermission('CytScreeners')" class="field-label">
                <b>By</b>
              </div>
              <tag-input
                v-if="hasPermission('CytScreeners')"
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :dataSource="screenersDataSource"
                :displayExpr="userDisplayName"
                valueExpr="id"
                searchExpr="displayName"
                v-model="searchFields.cytScreeners"
                :grayOutExpired="true"
              />
            </div>
            <div
              class="row"
              v-if="hasPermission('CytLastReviewedDate') || hasPermission('CytReviewers')"
            >
              <div class="col-2 field-label"><b>Last Reviewed</b></div>
              <date-range
                v-if="hasPermission('CytLastReviewedDate')"
                ref="cytLastReviewedDateRange"
                class="col-5 input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                placeholder="Start Date"
                name="cytLastReviewedDateAfter"
                @input="handleCytLastReviewedDateRange"
                :start="searchFields.cytLastReviewedDateAfter"
                :end="searchFields.cytLastReviewedDateBefore"
              />
              <div v-if="hasPermission('CytReviewers')" class="field-label">
                <b>By</b>
              </div>
              <tag-input
                v-if="hasPermission('CytReviewers')"
                class="col input-field"
                :noLabel="true"
                :highlightSearchValue="true"
                :dataSource="reviewersDataSource"
                :displayExpr="userDisplayName"
                valueExpr="id"
                searchExpr="displayName"
                v-model="searchFields.cytReviewers"
              />
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-start mb-2">
          <h2 class="mb-2">Columns to Export</h2>
          <button class="btn btn-primary ml-2" @click.prevent="selectAllColumns">Select All</button>
          <button class="btn btn-primary ml-2" @click.prevent="clearAllColumns">Clear All</button>
        </div>
        <div class="d-flex justify-content-start">
          <div v-for="category of Object.keys(exportFieldCategories)" v-bind:key="category">
            <h3>{{ category }}</h3>
            <div
              v-for="{ field, id } of getCategoryItems(exportFieldCategories[category])"
              v-bind:key="id"
              class="ml-2 pr-2"
            >
              <Checkbox
                :label="startCaseOverride(field, id)"
                :name="field"
                :id="field + 'Box'"
                :value="searchFields.exportFields.split(',').includes(id.toString())"
                @input="toggleField(field)"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
    <modal :status="saveModalOpen" @close="closeSaveSearchModal">
      <h2>Save Search Filter</h2>
      <text-input v-model="searchName" label="Name" v-focus />
      <div class="ml-auto my-2 my-lg-0 d-flex">
        <button class="btn btn-danger mr-1" @click="closeSaveSearchModal">Cancel</button>
        <button class="btn btn-success" @click="saveSearch">Save</button>
      </div>
    </modal>
    <modal :status="isExportsPopupOpen" @close="toggleExportsPopup">
      <h3>Download Export</h3>
      <select-input
        label="Exports"
        :dataSource="exportsDataSource"
        v-model="exportToDownload"
        :displayExpr="exportDisplayExpr"
      />
      <div class="ml-auto my-2 my-lg-0 d-flex">
        <Loader v-if="isDownloading" size="small" />
        <button class="btn btn-danger mr-1" @click="toggleExportsPopup">Cancel</button>
        <button class="btn btn-primary" @click="handleDownload">Download</button>
      </div>
    </modal>
  </div>
</template>

<script>
import DataSource from "devextreme/data/data_source";
import { mapGetters, mapState } from "vuex";
import {
  BillingApi,
  CasesApi,
  DropdownApi,
  MacrosApi,
  PrefixApi,
  ProvidersApi,
  UsersApi,
  ExportsApi,
  ProceduresApi,
  AuditLogApi
} from "@/services";
import cytologyService, { CytologyEntities } from "@/services/cytology";
import {
  MacroTypeEnum,
  UserTypesEnum,
  AuditLogItems,
  CytReviewTypeEnum,
  CytCodeGroupEnum,
  enumToDropDown,
  CytTypeEnum
} from "@/modules/enums";
import eventBus, {
  EDIT_SAVED_SEARCH_COLUMNS,
  LOAD_SAVED_SEARCH,
  SAVE_SEARCH
} from "@/modules/eventBus";
import { sortBy, uniqBy } from "lodash";
import {
  CLEAR_FREE_TEXT_FILTERS,
  RENDER_FREE_TEXT_SEARCH,
  SET_FREE_TEXT_SEARCH_FILTERS
} from "@/modules/constants";
import TextInput from "./common/TextInput.vue";
import TagInput from "./common/TagInput.vue";
import DateRange from "./common/DateRange.vue";
import Checkbox from "./common/Checkbox.vue";
import Modal from "./common/Modal.vue";
import IconButton from "./common/IconButton.vue";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import DatePicker from "./common/DatePicker.vue";
import Loader from "./common/Loader.vue";
import PageTitle from "./common/PageTitle.vue";
import {
  dateRangeFilter,
  formatPhoneNumber,
  formatSSN,
  downloadBlob,
  utcToLocal,
  calculateAccessionNumberingWithoutPrevCentury,
  capitalize,
  createLogItem,
  findEnumValue
} from "@/modules/helpers";
import ArrayStore from "devextreme/data/array_store";
import moment from "moment";
import { handleErrors } from "@/modules/handleErrors";
import SelectInput from "@/components/common/SelectInput.vue";
import { startCase } from "lodash";
import { scanCaseBarcode } from "@/modules/scanCaseBarcode";
import { required } from "vuelidate/lib/validators";
import Icon from "@/components/common/Icon.vue";

export default {
  components: {
    TextInput,
    TagInput,
    DateRange,
    Checkbox,
    Modal,
    IconButton,
    DxDropDownButton,
    DatePicker,
    Loader,
    PageTitle,
    SelectInput,
    Icon
  },
  data() {
    return {
      isSearchOpen: true,
      isLoading: false,
      isExpanded: false,
      searchFields: {},
      defaultFields: {
        patientFieldContains: null,
        specimenSiteContains: null,
        providerLocationIds: [],
        providerLocationCities: [],
        providerLocationStates: [],
        providerLocationZipCodes: [],
        caseIdStart: null,
        caseIdEnd: null,
        caseTagIds: [],
        labLocationIds: [],
        prefixIds: [],
        prefixTagIds: [],
        bornOn: null,
        ageMinYears: null,
        ageMaxYears: null,
        genderIds: [],
        ssn: null,
        mrn: null,
        accountNum: null,
        orderNum: null,
        proxyUserIds: [],
        prediagnosis: null,
        diagnosis: null,
        interfaceDiagnoses: [],
        cptCodeIds: [],
        holdCodeIds: [],
        protocolIds: [],
        accessioners: [], //email
        grossers: [], //id
        resulters: [], //email
        modifiers: [], //email
        reviewers: [], // email
        pathologistIds: [],
        providerIds: [],
        icdCodeIds: [],
        resultsMacroIds: [],
        macroDescriptionStartsWith: "",
        modifiedOnOrAfter: null,
        modifiedOnOrBefore: null,
        grossedOnOrAfter: null,
        grossedOnOrBefore: null,
        resultedOnOrAfter: null,
        resultedOnOrBefore: null,
        firstSignedOnOrBefore: null,
        firstSignedOnOrAfter: null,
        lastSignedOnOrBefore: null,
        lastSignedOnOrAfter: null,
        receivedOnOrAfter: null,
        receivedOnOrBefore: null,
        accessionedOnOrAfter: null,
        accessionedOnOrBefore: null,
        collectedOnOrAfter: null,
        collectedOnOrBefore: null,
        reviewedOnOrAfter: null,
        reviewedOnOrBefore: null,
        caseStageId: 1,
        billingTypes: [],
        billingCycles: [],
        exportFields: Array.from({ length: 47 }, (_, i) => i + 1).join(","),
        includeSecondaryProviders: false,
        isReviewed: false,
        raceIds: [],
        searchPrimaryProviders: true,
        searchSecondaryProviders: false,
        cytDiagnosticMacrosIds: [],
        cytDiagnosticCodesIds: [],
        cytSeverityLevels: [],
        cytologyDiagnosticsFreeText: null,
        cytPrefixTypeIds: [],
        cytMolecPanelsIds: [],
        cytMolecTestsIds: [],
        cytMolecTestResultsIds: [],
        cytMolecTestingOnly: null,
        cytScreeners: [],
        cytReviewers: [],
        cytAgreedWithLastReview: null,
        cytScreenAgain: null,
        cytScreenDateAfter: null,
        cytScreenDateBefore: null,
        cytLastReviewedDateAfter: null,
        cytLastReviewedDateBefore: null,
        cytDiagnosticCodesAdequacy: [],
        cytDiagnosticCodesComment: [],
        cytDiagnosticCodesDiagnosis: [],
        cytDiagnosticCodesHormonal: [],
        cytDiagnosticCodesRecommendation: []
      },
      providers: {
        names: [],
        locations: [],
        cities: [],
        states: [],
        zips: []
      },
      users: {
        ids: [],
        emails: []
      },
      nameSearch: ["firstName", "lastName"],
      prefixTagOptions: [],
      userSearches: [],
      searchName: "",
      saveModalOpen: false,
      buttonAttr: {
        class: "split-button"
      },
      dropDownOptions: {
        width: "17rem"
      },
      loadedSearch: null,
      columnsToExportToSave: null,
      showExpired: false,
      tooltipLookups: {
        prefixes: [],
        prefixTags: [],
        pathologists: [],
        resultsMacros: [],
        cptCodes: [],
        icdCodes: [],
        holdCodes: [],
        interfaceDiagnoses: [],
        users: []
      },
      isExportsPopupOpen: false,
      exportToDownload: null,
      isDownloading: false,
      caseNumberStart: "",
      caseNumberEnd: "",
      isFromCaseValid: false,
      isToCaseValid: false,
      exportFieldsEnum: {
        CaseId: 1,
        CaseNumber: 2,
        PatientName: 3,
        PatientDateOfBirth: 4,
        PatientAge: 5,
        PatientGender: 6,
        PatientMRN: 7,
        PatientSSN: 8,
        ProviderName: 9,
        CollectedOn: 10,
        ReceivedOn: 11,
        ReportedOn: 12,
        OrderNumber: 13,
        AccountNumber: 14,
        PathologistName: 15,
        SpecimenId: 16, //formerly SpecimenOrder
        SpecimenInternalId: 17, //formerly SpecimenId
        CaseStatus: 18,
        Protocol: 19,
        Site: 20,
        Diagnosis: 21,
        Microscopic: 22,
        CaseNote: 23,
        Gross: 24,
        Clinical: 25,
        CaseTags: 26,
        LabLocation: 27,
        ProviderLocation: 28,
        ProviderCity: 29,
        ProviderState: 30,
        ProviderZipCode: 31,
        SignedOn: 32,
        AccessionedOn: 33,
        Prefix: 34,
        PrefixTags: 35,
        CptCodes: 36,
        HoldCodes: 37,
        AccessionedBy: 38,
        GrossedBy: 39,
        ResultedBy: 40,
        ModifiedBy: 41,
        IcdCodes: 42,
        ResultMacros: 43,
        SpecimenNote: 44,
        GrossedOn: 45,
        ResultedOn: 46,
        InterfaceDiagnosis: 47,
        NumberOfBlocks: 48,
        NumberOfSlides: 49,
        PatientPhoneNumber: 50,
        PatientInsurance: 51,
        MacroDescriptions: 52,
        billingCycles: 53,
        billingTypes: 54,
        caseTagText: 55,
        Orders: 56,
        LastSignedOn: 57,
        PatientId: 58,
        PatientAddress1: 59,
        PatientAddress2: 60,
        PatientCity: 61,
        PatientState: 62,
        PatientZipCode: 63,
        PatientLegacyLocation: 64,
        ReviewedOn: 65,
        ReviewedBy: 66,
        PatientRace: 67,
        SecondaryProviders: 68,
        PatientFirstName: 69,
        PatientLastName: 70,
        // Cyt Fields, but word cyt is redundant so startCaseOverride will adjust.
        CytDiagnosticMacro: 71,
        // CytDiagnosticCodes: 72,
        CytSeverityLevel: 73,
        CytMolecPanels: 74,
        CytMolecTests: 75,
        CytMolecTestsOnly: 76,
        CytReviewer: 77,
        CytScreener: 78,
        CytAgreesWithPrevious: 79,
        CytScreenAgain: 80,
        CytLastReviewedDate: 81,
        CytScreenedDate: 82,
        CytDiagnosticCodesAdequacy: 83,
        CytDiagnosticCodesComment: 84,
        CytDiagnosticCodesDiagnosis: 85,
        CytDiagnosticCodesHormonal: 86,
        CytDiagnosticCodesRecommendation: 87
      },
      caseStageOptions: [
        { id: 1, displayName: "All Cases" },
        { id: 2, displayName: "Ready to Result" },
        { id: 3, displayName: "Not Signed" },
        { id: 4, displayName: "Signed" }
      ],
      billingCycleOptions: [],
      yesNoOptions: [
        { id: true, displayName: "Yes" },
        { id: false, displayName: "No" }
      ],
      exportFieldCategories: {
        Case: [1, 2, 13, 15, 18, 23, 26, 55, 27, 34, 35, 37, 53, 54, 56],
        Patient: [3, 69, 70, 4, 5, 6, 67, 7, 8, 14, 50, 51, 58, 59, 60, 61, 62, 63],
        Specimen: [16, 17, 19, 20, 21, 22, 24, 25, 44, 36, 42, 43, 52, 47, 48, 49],
        Provider: [9, 28, 29, 30, 31, 64, 68],
        Users: [38, 39, 40, 41, 65, 66],
        Dates: [10, 11, 33, 45, 46, 32, 57, 12],
        Cytology: [71, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87]
      }
    };
  },
  mounted() {
    if (!this.states?.length) {
      this.$store.dispatch("dropdowns/getStates");
    }
    this.searchFields = {
      ...this.defaultFields
    };
    ProvidersApi.getProviders(this.currentLab).then(res => {
      this.providers.names = sortBy(
        [
          ...new Set(
            res.data.map(e => ({ displayName: e.lastName + ", " + e.firstName, id: e.id }))
          )
        ],
        e => e.displayName.toLowerCase()
      );
    });
    ProvidersApi.getLocations().then(res => {
      this.providers.locations = sortBy(
        res.data.map(loc => {
          return {
            id: loc.id,
            displayName: loc.name,
            providerIds: loc.providers.map(e => e.id)
          };
        }),
        e => e.displayName.toLowerCase()
      );
      this.providers.cities = sortBy([...new Set(res.data.map(e => e.city))], e =>
        e.toLowerCase()
      ).map((e, idx) => ({ displayName: e, id: idx }));
      this.providers.zips = [...new Set(res.data.map(e => e.zipCode))]
        .sort()
        .map((e, idx) => ({ displayName: e, id: idx }));
    });
    DropdownApi.getPrefixTags().then(res => {
      this.prefixTagOptions = sortBy(res, "displayName");
    });
    if (!this.interfaceDiagnosisOptions.length) {
      this.$store.dispatch("dropdowns/getInterfaceDiagnoses");
    }
    DropdownApi.getBillingCycles().then(res => {
      this.billingCycleOptions = res || [];
    });
    this.$store.dispatch("dropdowns/getBillingTypes");
    this.loadSearches();
    this.$store.dispatch("dropdowns/getRaces");
    eventBus.$on(EDIT_SAVED_SEARCH_COLUMNS, data => {
      const { columns, id } = data;
      if (id) {
        this.loadSearches();
        this.updateLoadedSearch(id);
      } else {
        this.saveModalOpen = true;
        this.columnsToExportToSave = columns;
      }
    });
  },
  beforeDestroy() {
    eventBus.$off(EDIT_SAVED_SEARCH_COLUMNS);
  },
  validations() {
    return {
      caseNumberEnd: {
        required,
        validCase: async value => (value ? this.isToCaseValid : true),
        greaterThanFrom: value => {
          value = value.replace(/[a-z]/gi, "");
          const matcher = /(\d{2})(\d{2})-(\d{7})/im;
          if (matcher.test(value) && matcher.test(this.caseNumberStart)) {
            const [fromYear, fromNumberSequence] = this.caseNumberStart
              .replace(/[a-z]/gi, "")
              .split("-");
            const [toYear, toNumberSequence] = value.split("-");
            return (
              parseInt(toYear) >= parseInt(fromYear) &&
              parseInt(toNumberSequence) >= parseInt(fromNumberSequence)
            );
          }
          return true;
        },
        hasPrefix: value => (value && !this.usePathologyNumbering ? this.hasPrefix(value) : true),
        inSamePrefixGroup: value =>
          value && !this.usePathologyNumbering ? this.inSamePrefixGroup : true
      },
      caseNumberStart: {
        required,
        validCase: async value => (value ? this.isFromCaseValid : true),
        hasPrefix: value => (value && !this.usePathologyNumbering ? this.hasPrefix(value) : true)
      }
    };
  },
  methods: {
    createCytDiagCodeSource(typeId, prefixTypeIds) {
      // Filter diagnostic codes based on molecular test prefix type selection
      const baseDataSource = new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytDiagnosticCodes),
        sort: "name"
      });
      let prefixFilters = [];
      // If molecular tests are selected and prefix types are selected, apply filtering
      if (prefixTypeIds && prefixTypeIds.length > 0) {
        // Apply filter based on prefix types - assuming diagnostic codes have a prefixTypeId field
        // This may need to be adjusted based on actual API structure
        prefixFilters = prefixTypeIds
          .map(id => this.createCytTypeFilters(id))
          .reduce((acc, curr) => {
            if (!acc.length) {
              return [curr];
            } else {
              acc.push("or");
            }
            return [...acc, curr];
          }, []);
      }

      if (prefixFilters.length) {
        const filter = [prefixFilters, "and", ["cytCodeGroupId", typeId]];
        baseDataSource.filter(filter);
      } else {
        // If no prefix type filters, still need to filter by diagnostic type (e.g
        baseDataSource.filter(["cytCodeGroupId", typeId]);
      }

      return baseDataSource;
    },
    createCytTypeFilters(cytTypeId) {
      let filters = [];
      if (cytTypeId === CytTypeEnum.GynecologicalCytology) {
        // For gynecological cytology, include both Gynecological and Thin-Prep And Gynecological codes
        filters.push([
          ["cytTypeId", CytTypeEnum.GynecologicalCytology],
          "or",
          ["cytTypeId", CytTypeEnum["Thin Prep And Gynecological"]],
          "or",
          ["cytTypeId", CytTypeEnum["Hybrid Gynecological/Non-Gynecological Cytology"]]
        ]);
      } else if (cytTypeId === CytTypeEnum["Thin-Prep"]) {
        // For Thin-Prep, include both Thin-Prep and Thin-Prep And Gynecological codes
        filters.push([
          ["cytTypeId", CytTypeEnum["Thin-Prep"]],
          "or",
          ["cytTypeId", CytTypeEnum["Thin Prep And Gynecological"]]
        ]);
      } else if (cytTypeId === CytTypeEnum["Thin Prep And Gynecological"]) {
        // For Thin-Prep And Gynecological, include all three types
        filters.push([
          ["cytTypeId", CytTypeEnum["Thin-Prep"]],
          "or",
          ["cytTypeId", CytTypeEnum.GynecologicalCytology],
          "or",
          ["cytTypeId", CytTypeEnum["Thin Prep And Gynecological"]],
          "or",
          ["cytTypeId", CytTypeEnum["Hybrid Gynecological/Non-Gynecological Cytology"]]
        ]);
      } else if (
        cytTypeId === CytTypeEnum["Non-Gynecological Cytology"] ||
        cytTypeId === CytTypeEnum["Hybrid Gynecological/Non-Gynecological Cytology"]
      ) {
        // For Non-Gynecological Cytology, include both Hybrid and Non-Gynecological codes
        filters.push([
          ["cytTypeId", CytTypeEnum["Non-Gynecological Cytology"]],
          "or",
          ["cytTypeId", CytTypeEnum["Hybrid Gynecological/Non-Gynecological Cytology"]]
        ]);
      } else if (cytTypeId) {
        filters.push(["cytTypeId", "=", cytTypeId]);
      }
      return filters;
    },
    async handleCreateSearch() {
      if (!this.checkFields(this.searchFields)) {
        window.alert("Please enter a search criteria.");
        return;
      }
      if (!this.hasDatesSelected && !this.hasKeyField) {
        const keyFieldNames = [
          "Patient Name",
          "Accession # Range",
          "SSN",
          "MRN",
          "Account #",
          "Order #"
        ];
        window.alert(
          `Please select at least one date range or one of the below fields to search.<br><br>${keyFieldNames.join(
            "<br>"
          )}`,
          "warning"
        );
        return;
      }
      const invalidDateRanges = this.checkDateRanges();
      if (invalidDateRanges.length) {
        const isPlural = invalidDateRanges.length > 1;
        window.alert(
          `The selected date range${isPlural ? "s" : ""} for ${invalidDateRanges
            .map(e => capitalize(e))
            .join(", ")} ${isPlural ? "are" : "is"} greater than the range allowed by the lab (${
            this.labSettings.MaxSearchDateRange || "365"
          } days). Please check your input.`
        );
        return;
      }
      this.isLoading = true;
      eventBus.$once(RENDER_FREE_TEXT_SEARCH, () => {
        this.isLoading = false;
      });
      eventBus.$emit(SET_FREE_TEXT_SEARCH_FILTERS, this.searchFields);
      this.$emit("submitSearch");
      const logItem = createLogItem({}, AuditLogItems.Other);
      logItem.pageOrGridViewed = "Search Screen";
      const logCommentObject = await this.createLogCommentObject(false);
      logItem.comments = `Ran Free Text Search\n${JSON.stringify(logCommentObject, null, 2)}`;
      AuditLogApi.insertLogMessage(logItem);
    },
    checkFields(obj) {
      const keysWithValue = [];
      for (const key in obj) {
        if (this.hasValue(key) && !["readyToRead", "isUnsigned"].includes(key)) {
          keysWithValue.push(key);
        }
      }
      return keysWithValue.length > 0;
    },
    clearFilters() {
      this.searchFields = { ...this.defaultFields };
      this.loadedSearch = null;
      eventBus.$emit(CLEAR_FREE_TEXT_FILTERS);
      eventBus.$emit(LOAD_SAVED_SEARCH, null);
    },
    handleAccessionedRange(event) {
      this.searchFields.accessionedOnOrAfter = event[0];
      this.searchFields.accessionedOnOrBefore = event[1];
    },
    handleReceivedRange(event) {
      this.searchFields.receivedOnOrAfter = event[0];
      this.searchFields.receivedOnOrBefore = event[1];
    },
    handleCollectedRange(event) {
      this.searchFields.collectedOnOrAfter = event[0];
      this.searchFields.collectedOnOrBefore = event[1];
    },
    handleSignedDateRange(event) {
      this.searchFields.firstSignedOnOrAfter = event[0];
      this.searchFields.firstSignedOnOrBefore = event[1];
    },
    handleLastSignedDateRange(event) {
      this.searchFields.lastSignedOnOrAfter = event[0];
      this.searchFields.lastSignedOnOrBefore = event[1];
    },
    handleModifiedOnRange(event) {
      this.searchFields.modifiedOnOrAfter = event[0];
      this.searchFields.modifiedOnOrBefore = event[1];
    },
    handleReviewedOnRange(event) {
      this.searchFields.reviewedOnOrAfter = event[0];
      this.searchFields.reviewedOnOrBefore = event[1];
    },
    handleCytScreenDateRange(event) {
      this.searchFields.cytScreenDateAfter = event[0];
      this.searchFields.cytScreenDateBefore = event[1];
    },
    handleCytLastReviewedDateRange(event) {
      this.searchFields.cytLastReviewedDateAfter = event[0];
      this.searchFields.cytLastReviewedDateBefore = event[1];
    },
    getProviders() {
      ProvidersApi.getProviders(this.currentLab).then(res => {
        return res;
      });
    },
    nameDisplay: ({ firstName, lastName }) => lastName + ", " + firstName,
    openSaveSearch(event) {
      event.preventDefault();
      if (event.target.tagName === "BUTTON") {
        if (this.loadedSearch) {
          this.searchName = this.loadedSearch.name;
        }
        this.saveModalOpen = true;
      }
    },
    async loadSearches() {
      const searches = await UsersApi.getUserSearches();
      this.userSearches = sortBy([...searches], "name");
      // const searchStates = this.userSearches.map(e => JSON.parse(e.state));
      // function getUniqueEntries(fieldNameArray) {
      //   const returnArray = [];
      //   for (const fieldName of fieldNameArray) {
      //     for (const searchData of searchStates) {
      //       if (searchData.fields[fieldName]?.length) {
      //         for (const item of searchData.fields[fieldName]) {
      //           if (!returnArray.includes(item)) {
      //             returnArray.push(item);
      //           }
      //         }
      //       }
      //     }
      //   }
      //   return returnArray;
      // }
      // function createFilter(array, valueExpr = "id") {
      //   return array.length > 1
      //     ? array.reduce((acc, curr) => {
      //         if (Array.isArray(acc)) {
      //           return [...acc, "or", [valueExpr, "=", curr]];
      //         }
      //         return [[valueExpr, "=", acc], "or", [valueExpr, "=", curr]];
      //       })
      //     : [valueExpr, "=", array[0]];
      // }
      // const prefixesInSearch = getUniqueEntries(["prefixIds"]);
      // if (prefixesInSearch.length) {
      //   PrefixApi.searchStore
      //     .load({
      //       filter: createFilter(prefixesInSearch)
      //     })
      //     .then(res => {
      //       this.tooltipLookups.prefixes = res;
      //     });
      // }
      // const pathologistsInSearch = getUniqueEntries(["pathologistIds", "proxyUserIds"]);
      // if (pathologistsInSearch.length) {
      //   DropdownApi.searchPathologists
      //     .load({ filter: createFilter(pathologistsInSearch) })
      //     .then(res => {
      //       this.tooltipLookups.pathologists = res;
      //     });
      // }
      // const resultsMacrosInSearch = getUniqueEntries(["resultsMacroIds"]);
      // if (resultsMacrosInSearch.length) {
      //   MacrosApi.searchStore
      //     .load({
      //       filter: [
      //         ["macroType", MacroTypeEnum.Results],
      //         "and",
      //         createFilter(resultsMacrosInSearch, "macroId")
      //       ]
      //     })
      //     .then(res => {
      //       this.tooltipLookups.resultsMacros = res;
      //     });
      // }
      // const cptCodesInSearch = getUniqueEntries(["cptCodeIds"]);
      // if (cptCodesInSearch.length) {
      //   DropdownApi.searchCptCodes.load({ filter: createFilter(cptCodesInSearch) }).then(res => {
      //     this.tooltipLookups.cptCodes = res;
      //   });
      // }
      // const icdCodesInSearch = getUniqueEntries(["icdCodeIds"]);
      // if (icdCodesInSearch.length) {
      //   DropdownApi.searchIcdCodes.load({ filter: createFilter(icdCodesInSearch) }).then(res => {
      //     this.tooltipLookups.icdCodes = res;
      //   });
      // }
      // const holdCodesInSearch = getUniqueEntries(["holdCodeIds"]);
      // if (cptCodesInSearch.length) {
      //   DropdownApi.searchHoldCodes.load({ filter: createFilter(holdCodesInSearch) }).then(res => {
      //     this.tooltipLookups.holdCodes = res;
      //   });
      // }
      // UsersApi.getAllUsers().then(res => {
      //   this.tooltipLookups.users = res.data;
      // });
    },
    closeSaveSearchModal() {
      this.saveModalOpen = false;
      this.searchName = "";
      this.columnsToExportToSave = null;
    },
    async saveSearch() {
      if (!this.searchName) {
        window.alert("Please enter a name.");
        return;
      }
      if (this.nameInUse) {
        const confirm = await window.confirm(
          `This name is already in use. Overwrite existing search named "${this.searchName}"?`
        );
        if (!confirm) {
          return;
        }
        const savedSearchToUpdate = this.userSearches.find(
          e => e.name.toLowerCase() === this.searchName.toLowerCase()
        );
        const savedState = savedSearchToUpdate.state;
        const currentState = JSON.stringify({
          fields: this.searchFields,
          gridState: this.searchGridState,
          columnsToExport: savedSearchToUpdate.columnsToExport
        });
        if (savedState === currentState) {
          window.alert(`No changes have been made to ${this.loadedSearch.name}.`);
          return;
        }
        try {
          await UsersApi.updateSavedSearch({
            ...savedSearchToUpdate,
            state: JSON.stringify({
              fields: this.searchFields,
              gridState: this.searchGridState,
              columnsToExport: this.loadedSearch.columnsToExport
            })
          }).then(res => eventBus.$emit(SAVE_SEARCH, res));
          window.notify(`Updated ${this.loadedSearch.name}.`);
          this.loadSearches();
          this.closeSaveSearchModal();
        } catch (error) {
          window.notify("An error ocurred.", "error");
        }
      } else {
        try {
          await UsersApi.createSavedSearch({
            name: this.searchName,
            state: JSON.stringify({
              fields: this.searchFields,
              gridState: this.searchGridState,
              columnsToExport:
                this.columnsToExportToSave || this.loadedSearch?.columnsToExport || null
            })
          }).then(res => {
            this.loadSearch({ itemData: res });
            eventBus.$emit(SAVE_SEARCH, res);
          });
          await this.loadSearches();
          this.closeSaveSearchModal();
        } catch (error) {
          window.notify("An error occured.", "error");
        }
      }
    },
    loadSearch({ itemData }) {
      try {
        let state = JSON.parse(itemData.state);
        state.fields = { ...this.defaultFields, ...state.fields };
        this.searchFields = state.fields;
        this.loadedSearch = {
          id: itemData.id,
          name: itemData.name,
          fields: state.fields,
          gridState: state.gridState,
          columnsToExport:
            state?.columnsToExport ||
            this.searchGridState.columns.map(e => {
              return {
                name: e.name,
                visible: [
                  "diagnosis",
                  "microscopic",
                  "notes",
                  "gross",
                  "caseNote",
                  "clinical"
                ].includes(e.name)
                  ? true
                  : e.visible,
                displayName: e.name
              };
            })
        };
        this.columnsToExportToSave = this.loadedSearch.columnsToExport;
        const payload = {
          name: "freeTextSearch",
          gridState: state.gridState,
          columns: state.gridState.columns
        };
        this.$store.dispatch("grids/setGridState", payload);
        eventBus.$emit(LOAD_SAVED_SEARCH, this.loadedSearch);
      } catch (error) {
        window.notify("An error occured.", "error");
      }
    },
    async deleteSavedSearch() {
      const confirm = await window.confirm(
        `Are you sure you want to permanently delete ${this.loadedSearch.name}?`
      );
      if (!confirm) {
        return;
      }
      try {
        await UsersApi.deleteUserSearch(this.loadedSearch.id);
        window.notify(`Deleted ${this.loadedSearch.name}`);
        this.loadSearches();
        this.loadedSearch = null;
        this.clearFilters();
      } catch (error) {
        window.notify("An error ocurred.", "error");
      }
    },
    setGridState(gridState) {
      const payload = {
        name: "freeTextSearch",
        gridState,
        columns: gridState.columns
      };
      this.$store.dispatch("grids/setGridState", payload);
    },
    updateLoadedSearch(id) {
      const searchToLoad = this.userSearches.find(e => e.id === id);
      this.loadSearch({ itemData: searchToLoad });
    },
    providerFullName({ lastName, firstName }) {
      return lastName + ", " + firstName;
    },
    handleGrossedRange(event) {
      this.searchFields.grossedOnOrAfter = event[0];
      this.searchFields.grossedOnOrBefore = event[1];
    },
    handleResultedOnRange(event) {
      this.searchFields.resultedOnOrAfter = event[0];
      this.searchFields.resultedOnOrBefore = event[1];
    },
    hasValue(property) {
      const value = this.searchFields[property];
      if (value !== null && value !== undefined && value !== "") {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return true;
      }
      return false;
    },
    userDisplayName(user) {
      return user.lastName + ", " + user.firstName;
    },
    async handleExport() {
      if (!this.searchFields.exportFields.length) {
        window.alert("Please select at least one column to export.");
        return;
      }
      if (!this.hasDatesSelected && !this.hasKeyField) {
        const keyFieldNames = [
          "Patient Name",
          "Accession # Range",
          "SSN",
          "MRN",
          "Account #",
          "Order #"
        ];
        window.alert(
          `Please select at least one date range or one of the below fields to search.<br><br>${keyFieldNames.join(
            "<br>"
          )}`,
          "warning"
        );
        return;
      }
      try {
        await CasesApi.exportSearch(this.searchFields);
        const { userName, phoneNumber, sendMfaCodeByEmail, sendMfaCodeBySms } = this.currentUser;
        const alertMessage = `Your export has been requested. You will receive
        ${sendMfaCodeByEmail ? "an email at " + userName : ""}
        ${sendMfaCodeByEmail && sendMfaCodeBySms ? " and " : ""}
        ${sendMfaCodeBySms ? "a text message at " + formatPhoneNumber(phoneNumber) : ""}
        when it is ready to download below.`;
        window.alert(alertMessage);
        const logItem = createLogItem({}, AuditLogItems.Other);
        logItem.pageOrGridViewed = "Search Screen";
        const logCommentObject = await this.createLogCommentObject(true);
        logItem.comments = `Ran case export\n${JSON.stringify(logCommentObject, null, 2)}`;
        AuditLogApi.insertLogMessage(logItem);
      } catch (error) {
        handleErrors(error);
      }
    },
    getItemTooltip(data) {
      const { fields } = JSON.parse(data.state);
      const dataForFields = [];
      // So that it displays a user-friendly name
      const fieldNameEnum = {
        prefixIds: "Prefixes",
        prefixTagIds: "Prefix Tags",
        specimenSiteContains: "Site",
        resultsMacroIds: "Results Macros",
        providerIds: "Provider Names",
        providerLocationIds: "Provider Locations",
        providerLocationCities: "Provider Cities",
        providerLocationStates: "Provider States",
        providerLocationZipCodes: "Provider ZIPs",
        pathologistIds: "Pathologists",
        accessioners: "Accessoned By", //email
        grossers: "Grossed By", //id
        resulters: "Resulted By", //email
        modifiers: "Modified After Reported By", //email
        cptCodeIds: "CPT Codes",

        holdCodeIds: "Hold Codes",
        isUnsigned: "Show Signed Cases",
        readyToRead: "Show Ready to Result Cases",
        patientFieldContains: "Patient Name",
        labLocationIds: "Lab Locations",
        bornOn: "Patient DOB",
        genderIds: "Patient Sexes",
        ssn: "Patient SSN",
        mrn: "Patient MRN",
        accountNum: "Account Number",
        orderNum: "Order Number",
        proxyUserIds: "Proxies",
        prediagnosis: "Pre-Diagnosis Text",
        diagnosis: "Diagnosis Text",
        icdCodeIds: "ICD Codes",
        interfaceDiagnoses: "Interface Diagnosis"
      };
      for (const fieldName of Object.keys(fields)) {
        // Date ranges
        if (fieldName.includes("OnOrBefore") && fields[fieldName]) {
          const fieldRangeName = fieldName.replace("OnOrBefore", "");
          dataForFields.push(
            `${fieldRangeName}: ${moment(fields[fieldRangeName + "OnOrAfter"]).format(
              "M/D/yyyy"
            )} - ${moment(fields[fieldRangeName + "OnOrBefore"]).format("M/D/yyyy")}`
          );
          // Arrays
        } else if (Array.isArray(fields[fieldName]) && fields[fieldName].length) {
          let arrayItems = fields[fieldName];
          let sourceArray = [];
          let valueExpr = "id";
          let displayExpr = "displayName";
          switch (fieldName) {
            case "providerIds":
              sourceArray = this.providers.names;
              break;
            case "providerLocationStates":
              sourceArray = this.states;
              break;
            case "genderIds":
              sourceArray = this.sexes;
              break;
            case "prefixIds":
              sourceArray = this.tooltipLookups.prefixes;
              displayExpr = "code";
              break;
            case "prefixTagIds":
              sourceArray = this.prefixTagOptions;
              break;
            case "pathologistIds":
            case "proxyUserIds":
              sourceArray = this.tooltipLookups.pathologists;
              break;
            case "resultsMacroIds":
              sourceArray = this.tooltipLookups.resultsMacros;
              valueExpr = "macroId";
              displayExpr = "macroName";
              break;
            case "cptCodes":
              sourceArray = this.tooltipLookups.cptCodes;
              break;
            case "icdCodes":
              sourceArray = this.tooltipLookups.icdCodes;
              displayExpr = "displayCode";
              break;
            case "holdCodes":
              sourceArray = this.tooltipLookups.holdCodes;
              break;
            case "accessioners":
            case "resulters":
            case "modifiers":
              sourceArray = this.tooltipLookups.users;
              valueExpr = "guid";
              break;
            case "grossers":
              sourceArray = this.tooltipLookups.users;
              valueExpr = "username";
              break;
            default:
              arrayItems = fields[fieldName];
          }
          if (sourceArray.length) {
            arrayItems = this.getDataForIds(arrayItems, sourceArray, valueExpr, displayExpr);
          } else {
            arrayItems = fields[fieldName];
          }
          dataForFields.push(`${fieldNameEnum[fieldName]}: ${arrayItems.join(", ")}`);
          // Everything else
        } else if (
          !Array.isArray(fields[fieldName]) &&
          fields[fieldName] &&
          !fieldName.includes("OnOrAfter")
        ) {
          dataForFields.push(`${fieldNameEnum[fieldName] || fieldName}: ${fields[fieldName]}`);
        }
      }
      return dataForFields.join(";<br>");
    },
    hasPermission(field) {
      const { permissions } = this;
      function checkPermission(suffix) {
        return permissions["CaseSearchBy" + suffix];
      }
      if (field === "PreDiagnosis") {
        return checkPermission("SpecimenGross") || checkPermission("SpecimenClinical");
      }
      if (field === "Diagnosis") {
        return (
          checkPermission("SpecimenDiagnosis") ||
          checkPermission("SpecimenMicroscopic") ||
          checkPermission("SpecimenNote") ||
          checkPermission("CaseNotes")
        );
      }
      return checkPermission(field);
    },
    getDataForIds(dataArray, lookupArray, valueExpr = "id", displayExpr = "displayName") {
      dataArray = dataArray.map(e => {
        const item = lookupArray.find(i => i[valueExpr] === e);
        if (item) {
          return item[displayExpr];
        }
        return e;
      });
      return dataArray;
    },
    toggleExportsPopup() {
      this.isExportsPopupOpen = !this.isExportsPopupOpen;
    },
    async handleDownload() {
      this.isDownloading = true;
      try {
        const exportData = await ExportsApi.searchExports.load({
          filter: ["id", "=", this.exportToDownload]
        });
        const file = await ExportsApi.downloadExport(this.exportToDownload);
        downloadBlob(
          file,
          `CASE_EXPORT_${moment(utcToLocal(exportData.requestedOn)).format("M-D-yyyy_h-mma")}`,
          "xlsx"
        );
      } catch (error) {
        if (error?.response?.data && error.response.data.includes("does not exist")) {
          window.alert("Export not currently available. Please try again later.");
        } else {
          handleErrors(error);
        }
      } finally {
        this.isDownloading = false;
      }
    },
    exportDisplayExpr(data) {
      if (data?.requestedOn) {
        return moment(utcToLocal(data.requestedOn)).format("M/D/yyyy h:mm a");
      }
      return "";
    },

    startCaseOverride(field, id) {
      // Adequacy, Comment, Diagnosis, Hormonal, and Recommendation fields should display the user-defined term if set, otherwise default to the field name. This is the only place in the app where these fields are displayed outside of a case details view, so it made the most sense to handle the override here.
      const diagnosticCodesFields = [83, 84, 85, 86, 87];
      if (diagnosticCodesFields.includes(id)) {
        const overrides = {
          83: this.adequacyTerm || "Adequacy",
          84: this.commentTerm || "Comment",
          85: this.diagnosisTerm || "Diagnosis",
          86: this.hormonalTerm || "Hormonal",
          87: this.recommendationTerm || "Recommendation"
        };
        return overrides[id];
      }
      if (/^cyt/gi.test(field)) {
        return startCase(field.replace(/^cyt/gi, ""));
      }
      return startCase(field);
    },
    async formatAccessionNumber(event) {
      const { value } = event.target;
      const targetField = event.target.name;
      let targetId = "";
      if (targetField === "caseNumberStart") {
        targetId = "caseIdStart";
      }
      if (targetField === "caseNumberEnd") {
        targetId = "caseIdEnd";
      }
      this.searchFields[targetId] = null;
      let prefix = "";
      if (value) {
        if (this.hasPrefix(value)) {
          const regex = /^(?<prefix>[a-z]{1,6})[0-9]/i.exec(value);
          prefix = regex.groups.prefix.toUpperCase();
        }
        let targetNumber = this.caseNumberWithoutPrefix(value);
        let number = targetNumber;
        if (targetNumber.replace("-", "").length < 11) {
          number = calculateAccessionNumberingWithoutPrevCentury(targetNumber);
        }
        if (number?.length === 12) {
          const fullNewNumber = scanCaseBarcode(prefix + number).caseNumber || prefix + number;
          this[targetField] = fullNewNumber;
          const matchingCases = await CasesApi.searchStore.load({
            filter: ["accessionNumber", prefix ? "=" : "contains", fullNewNumber]
          });
          if (matchingCases.length === 1) {
            const caseDetails = matchingCases[0];
            this.searchFields[targetId] = caseDetails.caseId;
            this[targetField] = caseDetails.accessionNumber;
            if (targetField === "caseNumberStart" && !this.caseNumberEnd) {
              this.searchFields.caseIdEnd = caseDetails.caseId;
              this.caseNumberEnd = caseDetails.accessionNumber;
              this.$nextTick(() => {
                this.$refs.caseNumberEnd.focus();
                this.$refs.caseNumberEnd.selectAll();
              });
            }
            if ((targetField === "caseNumberEnd") & !this.caseNumberStart) {
              this.searchFields.caseIdStart = caseDetails.caseId;
              this.caseNumberStart = caseDetails.accessionNumber;
              this.$nextTick(() => {
                this.$refs.caseNumberStart.focus();
                this.$refs.caseNumberStart.selectAll();
              });
            }
          } else {
            await window.alert("No matching case found.");
            this.searchFields[targetField] = "";
            this.$nextTick(() => {
              this.$refs[targetField].focus();
              this.$refs[targetField].selectAll();
            });
          }
        }
      }
    },
    hasPrefix(value) {
      if (value) {
        const regex = /^(?<prefix>[a-z]{1,6})[0-9]/i.exec(value);
        return Boolean(regex?.groups?.prefix);
      }
      return false;
    },
    caseNumberWithoutPrefix(caseNumber) {
      if (!caseNumber) {
        return "";
      }
      const regex = /^(?<prefix>[a-z]{1,6})[0-9]/i.exec(caseNumber);
      return caseNumber.replace(regex?.groups?.prefix, "");
    },
    async getCase(caseId) {
      this.isSearching = true;
      const caseDetails = await CasesApi.getCaseById(caseId);
      this.isSearching = false;
      return caseDetails;
    },
    toggleField(field) {
      const enumValue = this.exportFieldsEnum[field].toString();
      let fieldsToExport = this.searchFields.exportFields?.length
        ? this.searchFields.exportFields.split(",")
        : [];
      if (fieldsToExport.includes(enumValue)) {
        fieldsToExport = fieldsToExport.filter(e => e !== enumValue);
      } else {
        fieldsToExport.push(enumValue);
      }
      this.searchFields.exportFields = fieldsToExport.join(",");
    },
    enableExportField(fieldName) {
      const enumValue = this.exportFieldsEnum[fieldName].toString();
      let fieldsToExport = this.searchFields.exportFields?.length
        ? this.searchFields.exportFields.split(",")
        : [];
      if (!fieldsToExport.includes(enumValue)) {
        fieldsToExport.push(enumValue);
        this.searchFields.exportFields = fieldsToExport.join(",");
      }
    },
    selectAllColumns() {
      this.searchFields.exportFields = Array.from(
        { length: Object.keys(this.exportFieldsEnum).length },
        (_, i) => i + 1
      ).join(",");
    },
    clearAllColumns() {
      this.searchFields.exportFields = "";
    },
    createArrayStore(field) {
      return new DataSource({
        store: new ArrayStore({ data: this[field] })
      });
    },
    checkDateRanges() {
      let invalidRanges = [];
      for (const field of [
        "collected",
        "accessioned",
        "received",
        "grossed",
        "resulted",
        "signed",
        "modified"
      ]) {
        const ref = this.$refs[field + "Range"];
        if (ref && ref.isInvalidDateRange) {
          invalidRanges.push(field);
        }
      }
      return invalidRanges;
    },
    holdCodeDisplayExpr(holdCode) {
      if (this.labSettings.SearchByHoldCode && holdCode?.holdCode) {
        return `${holdCode.holdCode} - ${holdCode.displayName}`;
      }
      return holdCode.displayName;
    },
    procedureDisplayExpr(data) {
      return data && `${data.code} - ${data.description} `;
    },
    enumFieldsArray(columnHeight) {
      const numberOfEntries = Math.ceil(Object.keys(this.exportFieldsEnum).length / columnHeight);
      return Array.from(Array(numberOfEntries).keys()).map(e => (e + 1) * columnHeight);
    },
    toggleSecondaryProviders() {
      if (this.searchFields.searchPrimaryProviders && !this.searchFields.searchSecondaryProviders) {
        this.searchFields.searchPrimaryProviders = false;
        this.searchFields.searchSecondaryProviders = true;
      } else if (
        !this.searchFields.searchPrimaryProviders &&
        this.searchFields.searchSecondaryProviders
      ) {
        this.searchFields.searchPrimaryProviders = true;
      } else if (
        this.searchFields.searchPrimaryProviders &&
        this.searchFields.searchSecondaryProviders
      ) {
        this.searchFields.searchSecondaryProviders = false;
      }
    },
    async createLogCommentObject(includeExportFields) {
      // Filters out fields with no value
      let searchedFields = Object.keys(this.searchFields).filter(e => {
        if (!includeExportFields && e === "exportFields") {
          return false;
        }
        if (Array.isArray(this.searchFields[e])) {
          return this.searchFields[e].length;
        }
        return this.searchFields[e];
      });
      let returnObject = {};
      // Looks up displayName for field to add to payload
      function lookUpValue(value, lookupArray, displayExpr = "displayName", valueExpr = "id") {
        function getValue(id) {
          return lookupArray.find(e => e[valueExpr] === id)[displayExpr] || id;
        }
        if (Array.isArray(value)) {
          return value.map(e => getValue(e));
        }
        return getValue(value);
      }
      const getLookUpArrayAsync = async (
        values,
        dataSource,
        displayExpr = "displayName",
        valueExpr = "id"
      ) => {
        if (!values?.length) {
          return [];
        }
        let filter = [];
        for (let i = 0; i < values.length; i++) {
          filter.push([valueExpr, "=", values[i]]);
          if (i < values.length - 1) {
            filter.push("or");
          }
        }
        return await dataSource.load({
          filter,
          select: [valueExpr, displayExpr]
        });
      };
      const userData = await getLookUpArrayAsync(
        uniqBy([
          ...this.searchFields.accessioners,
          ...this.searchFields.grossers,
          ...this.searchFields.resulters,
          ...this.searchFields.modifiers
        ]).filter(e => /^[a-f0-9-]{32,36}$/i.test(e)),
        UsersApi.labUsersStore,
        "userName"
      );
      const macros = await getLookUpArrayAsync(
        [...this.searchFields.protocolIds, ...this.searchFields.resultsMacroIds],
        MacrosApi.searchStore,
        "macroName",
        "macroId"
      );
      const pathologists = await getLookUpArrayAsync(
        this.searchFields.pathologistIds,
        DropdownApi.searchPathologists
      );
      await Promise.all(
        searchedFields.map(async field => {
          let value = this.searchFields[field];
          let objectKey = field;
          let displayExpr = "displayName";
          let valueExpr = "id";
          // Converts dates to M/D/YYY
          if (
            value instanceof Date ||
            (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/?.test(value))
          ) {
            value = moment(value).format("M/D/YYYY");
          }
          const getLookUpArray = async () => {
            // If field has a lookup array, find that
            switch (field) {
              case "labLocationIds":
                return this.availableLabLocations;
              case "prefixIds":
                displayExpr = "code";
                return await getLookUpArrayAsync(
                  this.searchFields.prefixIds,
                  PrefixApi.searchStore,
                  "code"
                );
              case "prefixTagIds":
                return this.prefixTagOptions;
              case "genderIds":
                return this.sexes;
              case "providerIds":
                return this.providers.names;
              case "providerLocationIds":
                return this.providers.locations;
              case "providerLocationStates":
                return this.states;
              case "pathologistIds":
                return pathologists;
              case "protocolIds":
              case "resultsMacroIds":
                displayExpr = "macroName";
                valueExpr = "macroId";
                return macros;
              case "cptCodeIds":
                displayExpr = "code";
                return await getLookUpArrayAsync(
                  this.searchFields.cptCodeIds,
                  BillingApi.searchStore,
                  "code"
                );
              case "icdCodeIds":
                return await getLookUpArrayAsync(
                  this.searchFields.icdCodeIds,
                  DropdownApi.searchIcdCodes
                );
              case "caseTagIds":
                return await getLookUpArrayAsync(
                  this.searchFields.caseTagIds,
                  DropdownApi.searchTags
                );
              case "holdCodeIds":
                return await getLookUpArrayAsync(
                  this.searchFields.holdCodeIds,
                  DropdownApi.searchHoldCodes
                );
              case "procedureIds":
                displayExpr = "code";
                return await getLookUpArrayAsync(
                  this.searchFields.procedureIds,
                  ProceduresApi.getLabProceduresStore(this.currentLab),
                  "code"
                );
              case "billingTypes":
                return this.billingTypeOptions;
              case "billingCycles":
                return this.billingCycleOptions;
              case "caseStageId":
                return this.caseStageOptions;
              case "raceIds":
                return this.races;
              case "accessioners":
              case "grossers":
              case "resulters":
              case "modifiers":
                if (/^[a-f0-9-]{32,36}$/i.test(value)) {
                  displayExpr = "userName";
                  return userData;
                }
                break;
              default:
                return [];
            }
          };
          // Handle value and objectKey of relevant fields
          switch (field) {
            case "exportFields":
              // Gets value from enum
              value = value.split(",").map(e => findEnumValue(e, this.exportFieldsEnum));
              break;
            case "caseIdStart":
              value = this.caseNumberStart;
              objectKey = "caseNumberStart";
              break;
            case "caseIdEnd":
              value = this.caseNumberEnd;
              objectKey = "caseNumberEnd";
              break;
          }
          // If field has a lookup array, find value
          const lookUpArray = (await getLookUpArray()) || [];
          if (lookUpArray?.length) {
            value = lookUpValue(value, lookUpArray, displayExpr, valueExpr);
          }
          // Removes "Ids" or "Id from field name in return array"
          if (/Ids?$/i.test(objectKey)) {
            objectKey = objectKey.replace(/Ids$/i, "s").replace(/Id/i, "").replace(/xs$/, "xes");
          }
          returnObject[objectKey] = value;
        })
      );
      return returnObject;
    },
    getCategoryItems(values) {
      if (values?.length) {
        return values.map(e => {
          return { id: e, field: findEnumValue(e, this.exportFieldsEnum) };
        });
      }
      return [];
    }
  },
  computed: {
    ...mapState({
      currentLab: state => state.currentLab,
      currentUser: state => state.currentUser,
      casesSearchHeader: state => state.applicationSettings.casesSearchHeader,
      labSettings: state => state.labSettings,
      ApplyPathologistSearchFilter: state => state.labSettings.ApplyPathologistSearchFilter,
      states: state => state.dropdowns.states,
      searchGridState: state => state.grids.freeTextSearch,
      availableLabLocations: state => state.availableLabLocations,
      sexes: state => state.dropdowns.sexes,
      billingTypeOptions: state => state.dropdowns.billingTypes,
      interfaceDiagnosisOptions: state => state.dropdowns.interfaceDiagnoses,
      races: state => state.dropdowns.races,
      cytologyModuleEnabled: state => state.labSettings.CytologyModuleEnabled,
      adequacyTerm: state => state.labSettings.CytologyTermAdequacy,
      commentTerm: state => state.labSettings.CytologyTermComment,
      diagnosisTerm: state => state.labSettings.CytologyTermDiagnosis,
      reccomendationTerm: state => state.labSettings.CytologyTermRecommendation,
      hormonalTerm: state => state.labSettings.CytologyTermHormonal
    }),
    ...mapGetters(["permissions"]),

    cytologyDiagnosticCodeTypes() {
      const prefixTypeIds = this.searchFields.cytPrefixTypeIds || [];
      return [
        {
          id: CytCodeGroupEnum.Adequacy,
          label: this.adequacyTerm || "Adequacy",
          fieldName: "cytDiagnosticCodesAdequacy",
          dataSource: this.createCytDiagCodeSource(CytCodeGroupEnum.Adequacy, prefixTypeIds)
        },
        {
          id: CytCodeGroupEnum.Diagnosis,
          label: this.diagnosisTerm || "Diagnosis",
          fieldName: "cytDiagnosticCodesDiagnosis",
          dataSource: this.createCytDiagCodeSource(CytCodeGroupEnum.Diagnosis, prefixTypeIds)
        },
        {
          id: CytCodeGroupEnum.Recommendation,
          label: this.reccomendationTerm || "Recommendation",
          fieldName: "cytDiagnosticCodesRecommendation",
          dataSource: this.createCytDiagCodeSource(CytCodeGroupEnum.Recommendation, prefixTypeIds)
        },
        {
          id: CytCodeGroupEnum.Hormonal,
          label: this.hormonalTerm || "Hormonal",
          fieldName: "cytDiagnosticCodesHormonal",
          dataSource: this.createCytDiagCodeSource(CytCodeGroupEnum.Hormonal, prefixTypeIds)
        },
        {
          id: CytCodeGroupEnum.Comment,
          label: this.commentTerm || "Comment",
          fieldName: "cytDiagnosticCodesComment",
          dataSource: this.createCytDiagCodeSource(CytCodeGroupEnum.Comment, prefixTypeIds)
        }
      ];
    },
    hasDatesSelected() {
      const {
        firstSignedOnOrAfter,
        firstSignedOnOrBefore,
        lastSignedOnOrAfter,
        lastSignedOnOrBefore,
        receivedOnOrAfter,
        receivedOnOrBefore,
        accessionedOnOrAfter,
        accessionedOnOrBefore,
        collectedOnOrAfter,
        collectedOnOrBefore,
        modifiedOnOrAfter,
        modifiedOnOrBefore,
        grossedOnOrAfter,
        grossedOnOrBefore,
        resultedOnOrAfter,
        resultedOnOrBefore,
        reviewedOnOrAfter,
        reviewedOnOrBefore,
        cytScreenDateAfter,
        cytScreenDateBefore,
        cytLastReviewedDateAfter,
        cytLastReviewedDateBefore
      } = this.searchFields;
      if (
        (collectedOnOrAfter && collectedOnOrBefore) ||
        (accessionedOnOrAfter && accessionedOnOrBefore) ||
        (receivedOnOrAfter && receivedOnOrBefore) ||
        (firstSignedOnOrAfter && firstSignedOnOrBefore) ||
        (lastSignedOnOrAfter && lastSignedOnOrBefore) ||
        (modifiedOnOrAfter && modifiedOnOrBefore) ||
        (grossedOnOrAfter && grossedOnOrBefore) ||
        (resultedOnOrAfter && resultedOnOrBefore) ||
        (reviewedOnOrAfter && reviewedOnOrBefore) ||
        (cytScreenDateAfter && cytScreenDateBefore) ||
        (cytLastReviewedDateAfter && cytLastReviewedDateBefore)
      ) {
        return true;
      }
      return false;
    },
    hasCytologyFilters() {
      const cytFields = [
        "cytDiagnosticMacrosIds",
        "cytDiagnosticCodesAdequacy",
        "cytDiagnosticCodesDiagnosis",
        "cytDiagnosticCodesRecommendation",
        "cytDiagnosticCodesHormonal",
        "cytDiagnosticCodesComment",
        "cytSeverityLevels",
        "cytologyDiagnosticsFreeText",
        "cytPrefixTypeIds",
        "cytMolecPanelsIds",
        "cytMolecTestsIds",
        "cytMolecTestResultsIds",
        "cytMolecTestingOnly",
        "cytScreeners",
        "cytReviewers",
        "cytAgreedWithLastReview",
        "cytScreenAgain",
        "cytScreenDateAfter",
        "cytScreenDateBefore",
        "cytLastReviewedDateAfter",
        "cytLastReviewedDateBefore"
      ];

      return cytFields.some(field => this.hasValue(field));
    },
    hasCytTestSelected() {
      if (this.searchFields?.cytMolecTestsIds?.length) {
        return true;
      }
      return false;
    },
    hasKeyField() {
      if (this.searchFields.caseIdStart && this.searchFields.caseIdEnd) {
        return true;
      }
      let hasField = false;
      for (const field of ["patientFieldContains", "ssn", "mrn", "accountNum", "orderNum"]) {
        if (this.searchFields[field]) {
          hasField = true;
          break;
        }
      }
      return hasField;
    },
    providersDataSource() {
      return new DataSource({
        store: ProvidersApi.searchProviders,
        sort: "lastName"
      });
    },
    pathologistsDataSource() {
      return new DataSource({
        store: this.showExpired
          ? DropdownApi.searchPathologistsIncludingInactive
          : DropdownApi.searchPathologists,
        sort: "displayName"
      });
    },
    userDataSource() {
      return new DataSource({
        store: UsersApi.labUsersStore,
        sort: "lastName",
        filter: this.showExpired
          ? ["userTypeId", "=", UserTypesEnum.LabUser]
          : [["userTypeId", "=", UserTypesEnum.LabUser], "and", ["isEnabled", "=", true]]
      });
    },
    screenersDataSource() {
      return new DataSource({
        store: UsersApi.labUsersStore,
        sort: "lastName",
        filter: this.showExpired
          ? [
              ["userTypeId", "=", UserTypesEnum.LabUser],
              "and",
              [
                ["cytReviewTypeId", CytReviewTypeEnum.Screener],
                "or",
                ["cytReviewTypeId", CytReviewTypeEnum.Pathologist]
              ]
            ]
          : [
              ["userTypeId", "=", UserTypesEnum.LabUser],
              "and",
              ["isEnabled", "=", true],
              "and",
              [
                ["cytReviewTypeId", CytReviewTypeEnum.Screener],
                "or",
                ["cytReviewTypeId", CytReviewTypeEnum.Pathologist]
              ]
            ]
      });
    },
    reviewersDataSource() {
      return new DataSource({
        store: UsersApi.labUsersStore,
        sort: "lastName",
        filter: this.showExpired
          ? [
              ["userTypeId", "=", UserTypesEnum.LabUser]
              // "and",
              // [
              //   ["cytReviewTypeId", CytReviewTypeEnum.Reviewer],
              //   "or",
              //   ["cytReviewTypeId", CytReviewTypeEnum.Pathologist]
              // ]
            ]
          : [
              ["userTypeId", "=", UserTypesEnum.LabUser],
              "and",
              ["isEnabled", "=", true]
              // "and",
              // [
              //   ["cytReviewTypeId", CytReviewTypeEnum.Reviewer],
              //   "or",
              //   ["cytReviewTypeId", CytReviewTypeEnum.Pathologist]
              // ]
            ]
      });
    },
    prefixDataSource() {
      return new DataSource({
        store: PrefixApi.searchStore,
        sort: "code",
        filter: this.showExpired ? null : dateRangeFilter()
      });
    },
    resultsMacrosDataSource() {
      return new DataSource({
        store: MacrosApi.searchStore,
        filter: this.showExpired
          ? ["macroType", MacroTypeEnum.Results]
          : [
              dateRangeFilter("effectiveOn", "expiresOn"),
              "and",
              ["macroType", MacroTypeEnum.Results]
            ],
        sort: "macroName"
      });
    },
    cptCodesDataSource() {
      return new DataSource({
        store: BillingApi.searchStore,
        sort: "code",
        filter: this.showExpired ? null : dateRangeFilter()
      });
    },
    icdDataSource() {
      return new DataSource({
        store: DropdownApi.searchIcdCodes
        // filter: this.showExpired ? null : dateRangeFilter("effectiveOn", "expiryOn")
      });
    },
    caseTagDataSource() {
      const tagFilter = [`settingType`, "=", "T"];
      return new DataSource({
        store: DropdownApi.searchTags,
        filter: this.showExpired
          ? tagFilter
          : [tagFilter, "and", dateRangeFilter("effectiveOn", "expiryOn", true)],
        sort: "displayName"
      });
    },
    holdCodeDataSource() {
      return new DataSource({
        store: DropdownApi.searchHoldCodes,
        sort: this.labSettings.SearchByHoldCode ? "holdCode" : "displayName",
        filter: this.showExpired ? null : dateRangeFilter("effectiveOn", "expiryOn", true)
      });
    },
    interfaceDiagnosesDataSource() {
      return new DataSource({
        store: new ArrayStore({ data: this.interfaceDiagnosisOptions })
      });
    },
    protocolDataSource() {
      const protocolFilter = ["macroType", MacroTypeEnum.Protocol];
      return new DataSource({
        store: MacrosApi.searchStore,
        filter: this.showExpired
          ? protocolFilter
          : [protocolFilter, "and", dateRangeFilter("effectiveOn", "expiresOn")],
        sort: ["macroName"]
      });
    },
    nameInUse() {
      if (
        this.userSearches.map(e => e.name.toLowerCase()).includes(this.searchName.toLowerCase())
      ) {
        return true;
      }
      return false;
    },
    providerNamesList() {
      if (this.searchFields?.providerLocationIds?.length) {
        let providersAtSelectedLocations = [];
        for (const locationId of this.searchFields.providerLocationIds) {
          const locationData = this.providers.locations.find(e => e.id === locationId);
          for (const providerId of locationData.providerIds) {
            if (!providersAtSelectedLocations.find(e => e.id === providerId)) {
              providersAtSelectedLocations.push(
                this.providers.names.find(e => e.id === providerId)
              );
            }
          }
        }
        return sortBy(providersAtSelectedLocations, e => e.displayName.toLowerCase());
      }
      return this.providers.names;
    },
    locationsNamesList() {
      if (this.searchFields?.providerIds?.length) {
        let locationsWithSelectedProviders = [];
        for (const providerId of this.searchFields.providerIds) {
          const locationsWithProvider = this.providers.locations.filter(e =>
            e.providerIds.includes(providerId)
          );
          for (const location of locationsWithProvider) {
            if (!locationsWithSelectedProviders.find(e => e.id === location.id)) {
              locationsWithSelectedProviders.push(location);
            }
          }
        }
        return sortBy(locationsWithSelectedProviders, e => e.displayName.toLowerCase());
      }
      return this.providers.locations;
    },
    exportsDataSource() {
      return new DataSource({
        store: ExportsApi.searchExports,
        filter: ["requestedBy", "=", this.currentUser.userName] && ["exportType", "=", "Case"],
        sort: [{ selector: "requestedOn", desc: true }]
      });
    },
    procedureDataSource() {
      return new DataSource({
        store: ProceduresApi.getLabProceduresStore(this.currentLab),
        filter: this.showExpired ? null : dateRangeFilter(),
        sort: ["code", "description"]
      });
    },
    procedureSearchExpr() {
      return this.labSettings?.ProcedureSearchBy ? "description" : "code";
    },
    primaryTooltip() {
      const { searchPrimaryProviders, searchSecondaryProviders } = this.searchFields;
      if (searchPrimaryProviders && !searchSecondaryProviders) {
        return "Searching by Primary Provider only";
      }
      if (!searchPrimaryProviders && searchSecondaryProviders) {
        return "Searching by Secondary Provider only";
      }
      return "Searching by Primary and Secondary Providers";
    },
    hasMolecularTestSelected() {
      // Check if any molecular tests are selected
      return (
        (this.searchFields.cytMolecPanelsIds && this.searchFields.cytMolecPanelsIds.length > 0) ||
        (this.searchFields.cytMolecTestsIds && this.searchFields.cytMolecTestsIds.length > 0) ||
        (this.searchFields.cytMolecTestResultsIds &&
          this.searchFields.cytMolecTestResultsIds.length > 0)
      );
    },
    cytDiagnosticMacrosDataSource() {
      return new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytDiagnosticMacros),
        sort: "displayName"
      });
    },

    cytSeverityLevelsDataSource() {
      // Using a simple array for severity levels - this may need to be updated based on actual API
      return new DataSource({
        store: new ArrayStore({
          data: [
            { id: 1, displayName: "Low" },
            { id: 2, displayName: "Medium" },
            { id: 3, displayName: "High" },
            { id: 4, displayName: "Critical" }
          ]
        })
      });
    },
    cytPrefixTypesDataSource() {
      // Reuse existing prefix data source structure
      return enumToDropDown(CytTypeEnum);
    },
    cytMolecPanelsDataSource() {
      return new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytMolecPanels),
        sort: "displayText"
      });
    },
    cytMolecTestsDataSource() {
      return new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytMolecTests),
        sort: "displayText"
      });
    },
    cytMolecTestResultsDataSource() {
      return new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytMolecResults),
        sort: "displayText"
      });
    }
  },
  watch: {
    "searchFields.ssn": function (value) {
      if (value?.length) {
        this.searchFields.ssn = formatSSN(value);
      }
    },
    "searchFields.ageMinYears": {
      immediate: true,
      handler(nv) {
        this.searchFields.ageMinYears = nv ? parseInt(nv) : null;
      }
    },
    "searchFields.ageMaxYears": {
      immediate: true,
      handler(nv) {
        this.searchFields.ageMaxYears = nv ? parseInt(nv) : null;
      }
    },
    // Clear diagnostic codes when no molecular tests are selected
    hasMolecularTestSelected(newVal) {
      if (
        !newVal &&
        this.searchFields.cytDiagnosticCodesIds &&
        this.searchFields.cytDiagnosticCodesIds.length > 0
      ) {
        this.searchFields.cytDiagnosticCodesIds = [];
      }
    },
    // Auto-check cytology export fields when cytology filter fields are changed
    "searchFields.cytDiagnosticMacrosIds"(val) {
      if (val?.length) this.enableExportField("CytDiagnosticMacro");
    },
    "searchFields.cytDiagnosticCodesAdequacy"(val) {
      if (val?.length) this.enableExportField("CytDiagnosticCodesAdequacy");
    },
    "searchFields.cytDiagnosticCodesDiagnosis"(val) {
      if (val?.length) this.enableExportField("CytDiagnosticCodesDiagnosis");
    },
    "searchFields.cytDiagnosticCodesRecommendation"(val) {
      if (val?.length) this.enableExportField("CytDiagnosticCodesRecommendation");
    },
    "searchFields.cytDiagnosticCodesHormonal"(val) {
      if (val?.length) this.enableExportField("CytDiagnosticCodesHormonal");
    },
    "searchFields.cytDiagnosticCodesComment"(val) {
      if (val?.length) this.enableExportField("CytDiagnosticCodesComment");
    },
    "searchFields.cytSeverityLevels"(val) {
      if (val?.length) this.enableExportField("CytSeverityLevel");
    },
    "searchFields.cytMolecPanelsIds"(val) {
      if (val?.length) this.enableExportField("CytMolecPanels");
    },
    "searchFields.cytMolecTestsIds"(val) {
      if (val?.length) this.enableExportField("CytMolecTests");
    },
    "searchFields.cytMolecTestingOnly"(val) {
      if (val != null) this.enableExportField("CytMolecTestsOnly");
    },
    "searchFields.cytReviewers"(val) {
      if (val?.length) this.enableExportField("CytReviewer");
    },
    "searchFields.cytScreeners"(val) {
      if (val?.length) this.enableExportField("CytScreener");
    },
    "searchFields.cytAgreedWithLastReview"(val) {
      if (val != null) this.enableExportField("CytAgreesWithPrevious");
    },
    "searchFields.cytScreenAgain"(val) {
      if (val != null) this.enableExportField("CytScreenAgain");
    },
    "searchFields.cytLastReviewedDateAfter"(val) {
      if (val) this.enableExportField("CytLastReviewedDate");
    },
    "searchFields.cytScreenDateAfter"(val) {
      if (val) this.enableExportField("CytScreenedDate");
    }
  }
};
</script>

<style lang="scss" scoped>
.searchHeader {
  padding: 0.25rem;
}
.field-label {
  text-align: right;
  padding-right: 0px;
  margin-top: auto;
  margin-bottom: auto;
}
.input-field {
  min-height: 40px;
}
::v-deep .dropdown-button {
  .dx-icon::before,
  .dx-dropdowneditor-icon {
    color: white !important;
  }
}
</style>
